/**
 * Re-encode a recorded blob to 16 kHz / 16-bit / mono WAV (PCM) - the format Azure Speaker
 * Recognition expects for voiceprint enrolment + identification. Decodes via WebAudio, downmixes to
 * mono, linearly resamples to 16 kHz, and writes a canonical 44-byte WAV header + PCM16 body.
 *
 * (The check-in recorder encodes MP3 for in-call playback; voiceprints need PCM WAV instead, hence a
 * separate encoder. Same decode/downmix shape as encodeBlobToMonoMp3.)
 */

const TARGET_RATE = 16000;

export async function encodeWav16kMono(blob: Blob): Promise<Blob> {
  const arrayBuf = await blob.arrayBuffer();
  const AC: typeof AudioContext =
    window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new AC();
  let audioBuf: AudioBuffer;
  try {
    audioBuf = await ctx.decodeAudioData(arrayBuf.slice(0));
  } finally {
    void ctx.close();
  }

  const channels = audioBuf.numberOfChannels;
  const srcLen = audioBuf.length;
  if (srcLen === 0) throw new Error('Recording was empty');

  // Downmix to mono.
  const mono = new Float32Array(srcLen);
  for (let c = 0; c < channels; c++) {
    const data = audioBuf.getChannelData(c);
    for (let i = 0; i < srcLen; i++) mono[i] += data[i] / channels;
  }

  // Linear resample to 16 kHz.
  const srcRate = audioBuf.sampleRate;
  const ratio = TARGET_RATE / srcRate;
  const outLen = Math.max(1, Math.round(srcLen * ratio));
  const resampled = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const srcPos = i / ratio;
    const i0 = Math.floor(srcPos);
    const i1 = Math.min(i0 + 1, srcLen - 1);
    const frac = srcPos - i0;
    resampled[i] = mono[i0] * (1 - frac) + mono[i1] * frac;
  }

  // PCM16 body + 44-byte WAV header.
  const bytesPerSample = 2;
  const dataLen = outLen * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataLen);
  const view = new DataView(buffer);
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataLen, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, TARGET_RATE, true);
  view.setUint32(28, TARGET_RATE * bytesPerSample, true); // byte rate
  view.setUint16(32, bytesPerSample, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, dataLen, true);
  let offset = 44;
  for (let i = 0; i < outLen; i++) {
    const s = Math.max(-1, Math.min(1, resampled[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

/** Seconds of audio in a 16 kHz/16-bit/mono WAV produced by encodeWav16kMono. */
export function wavSeconds(blob: Blob): number {
  return Math.max(0, (blob.size - 44) / (TARGET_RATE * 2));
}
